import { CommonModule } from '@angular/common';
import { Component, EventEmitter, forwardRef, inject, Input, OnInit, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { LocaleSharedModule } from '@app/i18n/locale-shared.module';
import { DestroyableComponent } from '@models/destroyable.component';

import { ErrorNotificationService } from '@shared/error-notification/error-notification.service';
import { IconComponent } from '@shared/icon/icon.component';

import { distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-search-input',
  standalone: true,
  imports: [LocaleSharedModule, CommonModule, ReactiveFormsModule, IconComponent],
  templateUrl: './search-input.component.html',
  styleUrls: ['./search-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SearchInputComponent),
      multi: true,
    },
  ],
})
export class SearchInputComponent extends DestroyableComponent implements OnInit, ControlValueAccessor {
  errorNotificationService = inject(ErrorNotificationService);

  @Input() placeholder = 'Search...';
  @Input() maxlength: number;

  @Output() changed = new EventEmitter<string>();
  public expandSearch = false;
  public value: string;
  public isDisabled: boolean;
  public formControl = new UntypedFormControl('', {
    updateOn: 'change',
  });

  ngOnInit(): void {
    this.formControl.valueChanges
      .pipe(
        distinctUntilChanged(),
        filter((value) => value !== this.value),
      )
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (value) => {
          this.value = value;
          this.propagateChange(this.value);
          this.changed.emit(this.value);
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'OTHER-PROBLEM');
        },
      });
  }

  private propagateChange: any = () => {};
  private propagateTouched: any = () => {};

  writeValue(value: string): void {
    this.value = value;
    this.formControl.setValue(this.value);
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.propagateTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  onBlur(): void {
    this.propagateTouched();
  }

  public clearSearchForm() {}

  public onPressEnter(value: string) {
    this.value = value;
    this.propagateChange(this.value);
    this.changed.emit(this.value);
  }
}
